export const roles = {
	super_admins: [
		'Super Administrador'
	],
	admins: [
		'Administrador'
	],
	commercial_advisors: [
		'Asesor Comercial',
	],
	commercial_managers: [
		'Jefe comercial',
	],
	group_advisor: [
		'Asesor Grupal',
	],
	commercial_coordinators: [
		'Coordinador Comercial'
	],
	others: [
		'Director Comercial',
		'Coordinador Comercial',
		'Servicio Cliente',
		'Asistente Comercial',
		'RRHH'
	],
	human_resources: [
		'Recursos humanos'
	]
};

// Función helper para obtener el usuario del localStorage
const getCurrentUser = () => {
	try {
		const user = localStorage.getItem('user');
		return user ? JSON.parse(user) : null;
	} catch (error) {
		console.warn('Error parsing user from localStorage:', error);
		return null;
	}
};

// Función genérica para verificar roles
const hasRole = (roleCategory, userRole = null) => {
	const user = userRole ? {role: userRole} : getCurrentUser();

	if (!user || !user.role) return false;

	return roles[roleCategory] && roles[roleCategory].includes(user.role);
};

// Funciones específicas simplificadas
export const isSuperAdmin = (userRole = null) => hasRole('super_admins', userRole);
export const isAdmin = (userRole = null) => hasRole('admins', userRole);
export const isCommercialAdvisor = (userRole = null) => hasRole('commercial_advisors', userRole);
export const isCommercialManager = (userRole = null) => hasRole('commercial_managers', userRole);
export const isGroupAdvisor = (userRole = null) => hasRole('group_advisor', userRole);
export const isCommercialCoordinator = (userRole = null) => hasRole('commercial_coordinators', userRole);
export const isHumanResources = (userRole = null) => hasRole('human_resources', userRole);
export const isOther = (userRole = null) => hasRole('others', userRole);

// Función para verificar múltiples roles
export const hasAnyRole = (rolesList, userRole = null) => {
	if (!Array.isArray(rolesList)) {
		console.warn('hasAnyRole expects an array of roles');
		return false;
	}

	const user = userRole ? {role: userRole} : getCurrentUser();

	if (!user || !user.role) return false;

	return rolesList.includes(user.role);
};

// Función para verificar si tiene algún rol de una categoría específica
export const hasRoleInCategory = (category, userRole = null) => {
	return hasRole(category, userRole);
};

// Función para obtener todas las categorías de roles
export const getRoleCategories = () => Object.keys(roles);

// Función para obtener todos los roles disponibles
export const getAllRoles = () => Object.values(roles).flat();

// Función para obtener la categoría de un rol específico
export const getRoleCategory = (roleName) => {
	for (const [category, rolesList] of Object.entries(roles)) {
		if (rolesList.includes(roleName)) {
			return category;
		}
	}
	return null;
};

// Función para verificar si es administrador (super admin o admin)
export const isAnyAdmin = (userRole = null) => {
	return isSuperAdmin(userRole) || isAdmin(userRole);
};

// Función para verificar si es rol comercial
export const isCommercialRole = (userRole = null) => {
	return isCommercialAdvisor(userRole) ||
		isCommercialManager(userRole) ||
		isCommercialCoordinator(userRole);
};

// Función para obtener el rol actual del usuario
export const getCurrentUserRole = () => {
	const user = getCurrentUser();
	return user ? user.role : null;
};

// Función genérica para verificar múltiples categorías de roles
export const hasAnyRoleInCategories = (categories, userRole = null) => {
	if (!Array.isArray(categories)) {
		console.warn('hasAnyRoleInCategories expects an array of role categories');
		return false;
	}

	const user = userRole ? {role: userRole} : getCurrentUser();
	if (!user || !user.role) return false;

	// Verificar si el usuario tiene rol en alguna de las categorías especificadas
	return categories.some(category => {
		return roles[category] && roles[category].includes(user.role);
	});
};

// Función para verificar permisos basados en jerarquía
export const hasPermissionLevel = (requiredLevel, userRole = null) => {
	const hierarchy = {
		super_admins: 5,
		admins: 4,
		commercial_managers: 3,
		commercial_coordinators: 2,
		commercial_advisors: 1,
		others: 0
	};

	const user = userRole ? {role: userRole} : getCurrentUser();
	if (!user || !user.role) return false;

	const userCategory = getRoleCategory(user.role);
	const userLevel = hierarchy[userCategory] || 0;
	const requiredLevelValue = hierarchy[requiredLevel] || 0;

	return userLevel >= requiredLevelValue;
};

export default roles;